import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useGetSurveyStatisticsQuery } from "@/redux/service";
import BaseProgressBar from "@/components_v2/ProgressBars/BaseProgressBar";
import { SectionHeaderProps } from "../types";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";

const SectionHeader = ({ section }: SectionHeaderProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const sampleOrganizationId = searchParams.get("sampleOrganizationId");
  const currentOrganizationId = useOrganizationId();

  const surveyStatisticsResponse = useGetSurveyStatisticsQuery({
    survey: section.survey,
    section: section.id,
    organization: sampleOrganizationId || currentOrganizationId,
  });

  const statistics = useMemo(() => {
    const statisticsData = surveyStatisticsResponse.data;
    const currentSectionStatistics = statisticsData?.[0];
    if (!currentSectionStatistics) {
      return null;
    }

    const answeredQuestions =
      currentSectionStatistics.answeredRequiredQuestionsCount +
      currentSectionStatistics.answeredOptionalQuestionsCount;
    const totalQuestions =
      currentSectionStatistics.requiredQuestionsCount +
      currentSectionStatistics.optionalQuestionsCount;

    return {
      ...currentSectionStatistics,
      answeredQuestions,
      totalQuestions,
    };
  }, [surveyStatisticsResponse.data]);

  return (
    <div className="flex items-start self-stretch gap-400">
      <div
        className="section-header-image-container full-height flex-shrink-0 content-center"
        style={{ background: section.color }}
      >
        {section.icon && <img src={section.icon} alt="section icon" />}
      </div>

      <div className="section-header-content flex flex-col gap-300 justify-between items-start full-height">
        <h1 className="header-l-medium">{section.title}</h1>
        <p>{section.description}</p>

        {statistics && (
          <BaseProgressBar
            value={statistics.answeredQuestions}
            total={statistics.totalQuestions}
            label={t("survey.labels.progressLabel", {
              answeredRequired: statistics.answeredRequiredQuestionsCount,
              totalRequired: statistics.requiredQuestionsCount,
              answeredOptional: statistics.answeredOptionalQuestionsCount,
              totalOptional: statistics.optionalQuestionsCount,
            })}
          />
        )}
      </div>
    </div>
  );
};

export default SectionHeader;
